import { type Node, type ReactFlowInstance } from '@xyflow/react';
import type { BaseNodeData } from '../types';
import {
	NODE_WIDTH,
	SEGMENT_NODE,
	STEP_NODE_COMPACT_HEIGHT,
	STEP_NODE_EXPANDED_MIN_HEIGHT,
	TRIGGER_NODE,
} from './layoutConstants';

function getFallbackNodeWidth(node: Node<BaseNodeData>): number {
	if (node.type === 'segment') {
		return SEGMENT_NODE.WIDTH;
	}
	if (node.type === 'trigger') {
		return TRIGGER_NODE.WIDTH;
	}
	return NODE_WIDTH;
}

function getFallbackNodeHeight(node: Node<BaseNodeData>): number {
	if (node.type === 'segment') {
		return SEGMENT_NODE.MIN_HEIGHT;
	}
	if (node.type === 'trigger') {
		return TRIGGER_NODE.HEIGHT;
	}

	const isExpanded = (node.data as { isExpanded?: unknown } | undefined)?.isExpanded === true;
	return isExpanded ? STEP_NODE_EXPANDED_MIN_HEIGHT : STEP_NODE_COMPACT_HEIGHT;
}

export function scheduleCenterOnNode(
	setCenter: ReactFlowInstance['setCenter'],
	node: Node<BaseNodeData>,
	options: { duration?: number; zoom?: number } = {}
): void {
	const measuredWidth = typeof node.measured?.width === 'number' ? node.measured.width : undefined;
	const measuredHeight = typeof node.measured?.height === 'number' ? node.measured.height : undefined;

	const nodeWidth = measuredWidth ?? node.width ?? getFallbackNodeWidth(node);
	const nodeHeight = measuredHeight ?? node.height ?? getFallbackNodeHeight(node);

	const x = node.position.x + nodeWidth / 2;
	const y = node.position.y + nodeHeight / 2;

	// Double RAF: lets React commit the new nodes/edges and React Flow apply transforms
	// before we attempt to move the viewport.
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			setCenter(x, y, {
				duration: options.duration ?? 300,
				zoom: options.zoom ?? 1,
			});
		});
	});
}
